import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserX, KeyRound, Lock, RefreshCw } from "lucide-react";

/**
 * iter154 — "Users without a password" support card.
 *
 * Lists every user document whose `password_hash` is missing, null, or
 * empty. On production these are the migrated users who cannot log in
 * until either:
 *   • they complete a self-service reset (click "Send reset link"), OR
 *   • the admin sets a password directly for them (click "Set
 *     password") and shares it out-of-band.
 *
 * List refreshes automatically after each action so we can watch the
 * migrated cohort shrink to zero as we work through them.
 */
export default function UsersWithoutPasswordCard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [setPwOpen, setSetPwOpen] = useState(false);
  const [setPwTarget, setSetPwTarget] = useState(null);
  const [setPwValue, setSetPwValue] = useState("");
  const [setPwBusy, setSetPwBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users/without-password");
      setUsers(data.users || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sendReset = async (user) => {
    setBusyId(user.id);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/send-reset`);
      toast.success(
        `Reset link sent to ${data.sent_to} — expires in ${data.expires_in_minutes} min`,
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const openSetPw = (user) => {
    setSetPwTarget({ id: user.id, email: user.email });
    setSetPwValue("");
    setSetPwOpen(true);
  };

  const confirmSetPw = async () => {
    if (!setPwTarget?.id) return;
    if (setPwValue.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSetPwBusy(true);
    try {
      await api.post(`/admin/users/${setPwTarget.id}/set-password`, {
        new_password: setPwValue,
      });
      toast.success(
        `Password set for ${setPwTarget.email}. Share the new password with them out-of-band.`,
      );
      setSetPwOpen(false);
      setSetPwValue("");
      setSetPwTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSetPwBusy(false);
    }
  };

  return (
    <section className="mt-16" data-testid="admin-users-without-password-section">
      <div className="mb-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
            <UserX className="h-5 w-5 text-amber-600" /> Users without a
            password{" "}
            <span
              className="text-sm text-zinc-500 font-medium"
              data-testid="admin-users-without-password-count"
            >
              ({users.length})
            </span>
          </h2>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Migrated users who have not yet completed a reset. They cannot
            log in until either they click a reset link or you set a
            password for them directly.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="admin-users-without-password-refresh"
          className="h-9 rounded-sm border-zinc-300 text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <p className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</p>
      ) : users.length === 0 ? (
        <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          ✓ Every user has a password set. Nothing to do here.
        </div>
      ) : (
        <div className="rounded-sm border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs font-bold uppercase tracking-[0.1em] text-zinc-600">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Pub / Client</th>
                <th className="text-left px-3 py-2">Registered</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  className="border-t border-zinc-100"
                  data-testid={`admin-nopw-row-${u.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2">{u.name || <span className="text-zinc-400 italic">—</span>}</td>
                  <td className="px-3 py-2">
                    {u.pub_slug || u.client_slug || (
                      <span className="text-zinc-400 italic">unassigned</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => sendReset(u)}
                        disabled={busyId === u.id}
                        data-testid={`admin-nopw-send-${u.id}`}
                        className="h-8 rounded-sm border-zinc-300 text-zinc-800 font-semibold tracking-tight hover:border-zinc-950 hover:text-zinc-950"
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1" />
                        {busyId === u.id ? "Sending…" : "Send reset link"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openSetPw(u)}
                        data-testid={`admin-nopw-set-pw-${u.id}`}
                        className="h-8 rounded-sm border-amber-300 text-amber-800 font-semibold tracking-tight hover:border-amber-600 hover:bg-amber-50 hover:text-amber-900"
                      >
                        <Lock className="h-3.5 w-3.5 mr-1" />
                        Set password
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={setPwOpen} onOpenChange={setSetPwOpen}>
        <DialogContent data-testid="admin-nopw-set-pw-dialog">
          <DialogHeader>
            <DialogTitle>Set password directly</DialogTitle>
            <DialogDescription>
              Overwrites the user&apos;s stored password immediately. Share
              the new password with them by SMS, WhatsApp, or phone — this
              endpoint does NOT email it to them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                Target user
              </Label>
              <p className="mt-1 text-sm font-mono">
                {setPwTarget?.email || ""}
              </p>
            </div>
            <div>
              <Label
                htmlFor="admin-nopw-set-pw-input"
                className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500"
              >
                New password (min 8 chars)
              </Label>
              <Input
                id="admin-nopw-set-pw-input"
                type="text"
                value={setPwValue}
                onChange={(e) => setSetPwValue(e.target.value)}
                placeholder="e.g. TempPass2026!"
                data-testid="admin-nopw-set-pw-input"
                autoComplete="off"
                spellCheck={false}
                className="mt-2 h-11 rounded-sm border-zinc-300"
              />
              <p className="mt-1 text-xs text-zinc-500">
                Type it visibly so you can copy-paste to the user.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSetPwOpen(false)}
              disabled={setPwBusy}
              data-testid="admin-nopw-set-pw-cancel"
              className="h-11 rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmSetPw}
              disabled={setPwBusy || setPwValue.length < 8}
              data-testid="admin-nopw-set-pw-confirm"
              className="h-11 rounded-sm bg-amber-600 hover:bg-amber-700 text-white font-semibold tracking-tight"
            >
              {setPwBusy ? "Saving…" : "Set password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
